import { type SetStateAction, useCallback, useRef, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import {
  Link,
  Navigate,
  useLocation,
  useParams,
  useSearchParams,
} from "react-router-dom";

import { useQuery } from "@tanstack/react-query";
import {
  Button,
  Comment,
  Divider,
  Header,
  Icon,
  Loader,
  Message,
  Pagination,
} from "semantic-ui-react";

import {
  getLocalStorageItem,
  localStorageIsSupported,
} from "simplydo/core/helpers";
import { initUseQuery } from "simplydo/core/query";
import { OpenAPI } from "simplydo/interfaces";
import { RichTextViewer } from "simplydo/react/richtext";

import api from "api";
import { useAppSelector } from "store";
import util from "utils/utils";

import ConnectionsManager from "components/lib/ConnectionsManager";

import { CommentInput } from "./CommentInput";
import DisscussionComment from "./DiscussionComment";

export type CommentState = {
  [key: string]: {
    message: string;
    media: Array<{
      value: string;
      name: string;
      signedDownloadRequest?: string;
      type: "image" | "file" | "gif";
      data?: any;
    }>;
    loading?: boolean;
  };
};

type EditingState = { [key: string]: { message: string; loading?: boolean } };

export const DiscussionBoard = () => {
  const { t } = useTranslation();
  const discussionId = useParams<{ discussionId: string }>().discussionId;
  const [params, setParams] = useState({ page: 1 });
  const inputRef = useRef<HTMLInputElement>(null);
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [commentState, setCommentState] = useState<CommentState>({
    new: { message: "", media: [], loading: false },
  });
  const [editingState, setEditingState] = useState<EditingState>({});

  const discussionCommentQuery = useQuery(
    initUseQuery(api.discussions.getComments, [discussionId, params], {
      isEnabled: !!discussionId,
      onSuccess: (response) => {
        api.invalidateQueries(api.discussions.getAll, [
          "group",
          response.discussion?.forId,
        ]);
        api.invalidateQueries(api.notifications.get, []);
      },
    }),
  );

  const forType = discussionCommentQuery.data?.discussion?.forType;
  const forId = discussionCommentQuery.data?.discussion?.forId;

  const groupQuery = useQuery(
    initUseQuery(api.groups.get, [forId!], {
      isEnabled: forType === "group" && !!forId,
    }),
  );

  const user = useAppSelector((state) => state.user);
  const canManage =
    forType === "group" &&
    util.hasPermission(user, "org.manageGroups", groupQuery.data?.organisation);
  const canPostNewComments = util.hasPermission(
    user,
    "discussion.postNewComments",
    discussionId,
    forId,
  );
  const canPostMedia = util.hasPermission(
    user,
    "discussion.postMedia",
    discussionId,
    forId,
  );

  const pinnedComment = discussionCommentQuery.data?.comments?.find(
    (c) => c.isPinned,
  );

  const postComment = useCallback(
    (parentCommentId = "new") => {
      const commentData = commentState[parentCommentId] || {
        message: "",
        media: [],
      };
      const data: {
        message: string;
        media: Array<{
          value: string;
          name: string;
          signedDownloadRequest?: string;
          type: "image" | "file" | "gif";
          data?: any;
        }>;
        parent?: string;
      } = { message: commentData.message, media: commentData.media };
      if (parentCommentId !== "new") {
        data.parent = parentCommentId;
      }

      setCommentState((prevState) => ({
        ...prevState,
        [parentCommentId]: { ...prevState[parentCommentId], loading: true },
      }));

      api.discussions.addComment(
        discussionId,
        data,
        () => {
          api.invalidateQueries(api.discussions.getComments, [discussionId]);

          setCommentState((prevState) => {
            const prevComment = prevState[parentCommentId] || {
              message: "",
              media: [],
            };
            return {
              ...prevState,
              [parentCommentId]: {
                ...prevComment,
                message: "",
                media: [],
                loading: false,
              },
            };
          });
        },
        (err) => {
          toast.error(err.message);
          setCommentState((prevState) => ({
            ...prevState,
            [parentCommentId]: {
              ...(prevState[parentCommentId] || { message: "", media: [] }),
              loading: false,
            },
          }));
        },
      );
    },
    [discussionId, commentState],
  );

  const approveComment = useCallback(
    (commentId) => {
      api.discussions.approveComment(
        discussionId,
        commentId,
        () => {
          api.invalidateQueries(api.discussions.getComments, [discussionId]);
        },
        (err) => toast.error(err.message),
      );
    },
    [discussionId],
  );

  const rejectComment = useCallback(
    (commentId) => {
      api.discussions.rejectComment(
        discussionId,
        commentId,
        () => {
          api.invalidateQueries(api.discussions.getComments, [discussionId]);
        },
        (err) => toast.error(err.message),
      );
    },
    [discussionId],
  );

  const deleteComment = useCallback(
    (commentId) =>
      util
        .confirm(
          t("feeds.comments.delete.title"),
          t("feeds.comments.delete.info"),
        )
        .then(() => {
          api.discussions.deleteComment(
            discussionId,
            commentId,
            () => {
              api.invalidateQueries(api.discussions.getComments, [
                discussionId,
              ]);
              toast(t("generic.typeDeleted", { type: "Comment" }));
            },
            (err) => toast.error(err.message),
          );
        })
        .catch(() => {}),
    [t, discussionId],
  );

  const pinComment = useCallback(
    (commentId) => {
      api.discussions.editComment(
        discussionId,
        commentId,
        { isPinned: true },
        () => {
          api.invalidateQueries(api.discussions.getComments, [discussionId]);
        },
        () => {},
      );
    },
    [discussionId],
  );

  const unpinComment = useCallback(
    (commentId) => {
      api.discussions.editComment(
        discussionId,
        commentId,
        { isPinned: false },
        () => {
          api.invalidateQueries(api.discussions.getComments, [discussionId]);
        },
        () => {},
      );
    },
    [discussionId],
  );

  const reactComment = useCallback(
    (commentId, emoji, adding) => {
      const commentReactionUpdater =
        adding ?
          api.discussions.commentAddReaction
        : api.discussions.commentRemoveReaction;
      commentReactionUpdater(
        discussionId,
        commentId,
        emoji,
        () => {
          api.invalidateQueries(api.discussions.getComments, [discussionId]);
        },
        () => {},
      );
    },
    [discussionId],
  );

  const editComment = useCallback(
    (commentId: string) => {
      const currentState = editingState[commentId];
      if (!currentState) {
        return;
      }

      setEditingState((prevState) => ({
        ...prevState,
        [commentId]: { ...prevState[commentId], loading: true },
      }));
      api.discussions.editComment(
        discussionId,
        commentId,
        { message: currentState.message },
        () => {
          api.invalidateQueries(api.discussions.getComments, [discussionId]);
          setEditingState((prevState) => {
            const nextState = { ...prevState };
            delete nextState[commentId];
            return nextState;
          });
        },
        (err) => {
          toast.error(err.message);
          setEditingState((prevState) => ({
            ...prevState,
            [commentId]: { ...prevState[commentId], loading: false },
          }));
        },
      );
    },
    [discussionId, editingState],
  );

  const startEditingComment = useCallback(
    (commentId: string, message: string) => {
      setEditingState((prevState) => ({
        ...prevState,
        [commentId]: { message, loading: false },
      }));
    },
    [],
  );

  const cancelEditingComment = useCallback((commentId: string) => {
    setEditingState((prevState) => {
      const nextState = { ...prevState };
      delete nextState[commentId];
      return nextState;
    });
  }, []);

  const updateEditingComment = useCallback(
    (commentId: string, message: string) => {
      setEditingState((prevState) => ({
        ...prevState,
        [commentId]: { ...prevState[commentId], message },
      }));
    },
    [],
  );

  const updateComment = useCallback(
    (
      parentCommentId = "new",
      commentOrUpdater:
        | CommentState["string"]
        | SetStateAction<CommentState["string"]>,
    ) => {
      setCommentState((prevState) => {
        if (typeof commentOrUpdater === "function") {
          const update = commentOrUpdater(
            prevState[parentCommentId] || { message: "", media: [] },
          );
          return { ...prevState, [parentCommentId]: update };
        } else {
          const prevComment = prevState[parentCommentId] || {
            message: "",
            media: [],
          };
          return {
            ...prevState,
            [parentCommentId]: { ...prevComment, ...commentOrUpdater },
          };
        }
      });
    },
    [],
  );

  const onPageChange = useCallback((newPage) => {
    setParams((prevParams) => ({ ...prevParams, page: newPage }));
  }, []);

  if (!discussionId) {
    return (
      <Message info>
        <Message.Header>
          {t("challenge.discussion.noDiscussionSelectedTitle")}
        </Message.Header>
        <p>{t("challenge.discussion.noDiscussionSelectedText")}</p>
      </Message>
    );
  }

  if (forType === "group" && !location.pathname.startsWith("/groups/")) {
    return (
      <Navigate
        replace
        to={`/groups/${forId}/discussions/${discussionId}?${searchParams.toString()}`}
      />
    );
  }

  if (discussionCommentQuery.isLoading) {
    return <Loader active inline="centered" />;
  }

  if (discussionCommentQuery.isError) {
    return (
      <Message negative>
        <Message.Header>
          {t("challenge.discussion.errorLoadingTitle")}
        </Message.Header>
        <p>{t("challenge.discussion.errorLoadingText")}</p>
      </Message>
    );
  }

  return (
    <div>
      <Header>{discussionCommentQuery.data?.discussion?.title}</Header>
      {discussionCommentQuery.data?.discussion?.description ?
        <>
          <RichTextViewer
            value={discussionCommentQuery.data?.discussion?.description || ""}
          />
          <Divider hidden />
        </>
      : null}

      {forType === "group" ?
        user?.eventSubscriptions?.groupDiscussions?.includes("email") ?
          null
        : <Message
            error
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div>
              <Icon name="bell slash" /> {t("discussion.alerts.off")}
            </div>
            <Button
              basic
              compact
              size="tiny"
              as={Link}
              to="/preferences/notifications#subscription-groups"
            >
              {t("notifications.update")}
            </Button>
          </Message>

      : null}

      {(
        localStorageIsSupported() &&
        !getLocalStorageItem("keepTeamsHandleAdvertisingOff") &&
        user?.ownerOrganisation?.enabledFeatures?.includes(
          "teamsHandleAdvertising",
        ) &&
        (!user?.enabledServices?.includes("microsoftTeams") ||
          searchParams.get("connectionSuccess"))
      ) ?
        <Message>
          <ConnectionsManager
            entity="user"
            entityId={user._id}
            serviceProvider="microsoft"
            service="microsoftTeams"
            contextId="userIntegration"
            subtitle="Let users message you via a Microsoft Teams handle next to your discussion posts. Please note that your email address forms part of your Teams handle link and may be viewable to others."
            connectionButtonText="Connect & show Teams handle"
            onConnect={() => {
              toast.success("Made Teams handle available");
            }}
            buttons={
              localStorageIsSupported() ?
                <Button
                  onClick={() => {
                    localStorage.setItem(
                      "keepTeamsHandleAdvertisingOff",
                      "true",
                    );
                    // This is irrelevant it just forces a rerender because localstorage itself doesn't trigger a rerender
                    setSearchParams((prevParams) => {
                      prevParams.set("thOff", "true");
                      return prevParams;
                    });
                  }}
                >
                  Keep turned off
                </Button>
              : null
            }
          />
        </Message>
      : null}

      {!pinnedComment && canManage ?
        <Message
          size="tiny"
          info
          style={{
            display: "inline-block",
            width: "100%",
            marginBottom: 10,
            marginTop: 0,
          }}
        >
          <Message.Header>
            <div>
              <Icon name="pin" />
              <span style={{ marginLeft: "2px" }}> Why not pin a post?</span>
            </div>
          </Message.Header>
          <p style={{ marginLeft: 25, marginBottom: 0 }}>
            Pinning a post can be useful to explain the purpose of the
            discussion and helps to drive user engagement. To pin a post, find
            an existing entry in the feed below and tap "Pin comment".
          </p>
        </Message>
      : null}
      {canPostNewComments || canManage ?
        <CommentInput
          inputRef={inputRef}
          forType={forType}
          forId={forId}
          discussionId={discussionId}
          currentReply={commentState.new}
          updateReply={(commentOrUpdater) =>
            updateComment("new", commentOrUpdater)
          }
          postComment={postComment}
          canPostMedia={canPostMedia || canManage}
        />
      : null}

      <Comment.Group
        style={{ display: "flex", flexDirection: "column", maxWidth: "100%" }}
      >
        {(discussionCommentQuery.data?.comments || []).map((item) => (
          <DisscussionComment
            key={item._id}
            forType={forType}
            forId={forId}
            discussionId={discussionId}
            comment={item}
            replies={discussionCommentQuery.data?.replies?.[item._id] || []}
            currentReply={commentState[item._id] || { message: "", media: [] }}
            canManage={canManage}
            updateReply={updateComment}
            postComment={() => postComment(item._id)}
            deleteComment={deleteComment}
            approveComment={approveComment}
            moderationEnabled={
              discussionCommentQuery.data?.discussion?.discussionModeration ||
              false
            }
            rejectComment={rejectComment}
            pinComment={pinComment}
            unpinComment={unpinComment}
            reactComment={reactComment}
            editingState={editingState}
            startEditingComment={startEditingComment}
            cancelEditingComment={cancelEditingComment}
            updateEditingComment={updateEditingComment}
            editComment={editComment}
          />
        ))}
      </Comment.Group>
      {(
        discussionCommentQuery.data?.previousPageAvailable ||
        discussionCommentQuery.data?.nextPageAvailable
      ) ?
        <Pagination
          onPageChange={(_e, { activePage }) =>
            onPageChange(activePage as number)
          }
          activePage={discussionCommentQuery.data?.page}
          totalPages={Math.ceil(discussionCommentQuery.data?.total / 20)}
          boundaryRange={1}
          siblingRange={1}
          ellipsisItem={null}
          firstItem={null}
          lastItem={null}
        />
      : null}
    </div>
  );
};
